import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Brotei: até quanto da altura do carrinho o resumo pode ocupar e continuar fixo embaixo.
   * O tema vinha com 0.5 (metade). Como o resumo da Brotei é mais alto (selo, Pix, parcelas e selos de confiança),
   * ele passava da metade no celular e o tema soltava o resumo, fazendo tudo rolar junto.
   * @type {number}
   */
  #summaryThreshold = 0.85;

  /** @type {ResizeObserver | null} */
  #summaryObserver = null;

  /** @type {Element | null} */
  #observedSummary = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#summaryObserver?.disconnect();
    this.#summaryObserver = null;
    this.#observedSummary = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Brotei: recalcula quando o resumo muda de altura com o carrinho aberto (cupom, quantidade, selo de desconto)
    this.#observeSummary(summary);

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (!drawerHeight) return; // carrinho fechado: não há o que medir

    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    const sticky = ratio > this.#summaryThreshold ? 'false' : 'true';
    if (dialog.getAttribute('cart-summary-sticky') !== sticky) {
      dialog.setAttribute('cart-summary-sticky', sticky);
    }
  };

  /**
   * @param {Element} summary
   */
  #observeSummary(summary) {
    if (typeof ResizeObserver === 'undefined' || this.#observedSummary === summary) return;
    this.#summaryObserver?.disconnect();
    this.#summaryObserver = new ResizeObserver(() => this.#updateStickyState());
    this.#summaryObserver.observe(summary);
    this.#observedSummary = summary;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}